import React, {useEffect, useMemo, useState} from 'react'
import {Modal, Spinner} from 'react-bootstrap'
import ComponentButton from '../../components/ComponentButton'
import {CryptoCert, CryptoProService} from './cryptoProService'
import api from '../../../api'
import {TSignModalWizard} from './_types'
import {autocompleteDocument} from '../../components/helpers'
import {useAuth} from '../../../modules/auth'
import {getErrorToast} from '../toasts'
import {useIntl} from 'react-intl'

const SignModalWizard: React.FC<TSignModalWizard> = ({
    show,
    onClose,
    visitId,
    formData,
    is_sign,
}) => {
    const {currentUser} = useAuth()
    const intl = useIntl()
    const [step, setStep] = useState<1 | 2>(1)
    const [isLoading, setLoading] = useState(false)
    const [htmlContent, setHtmlContent] = useState<string>('')
    const [pluginReady, setPluginReady] = useState(false)
    const [certs, setCerts] = useState<CryptoCert[]>([])
    const [selectedThumbprint, setSelectedThumbprint] = useState<string | null>(null)

    const cryptoService = useMemo(() => new CryptoProService(), [])

    useEffect(() => {
        if (!show) return

        setStep(1)
        setLoading(true)

        cryptoService.getCertificates().then((certs) => {
            setCerts(certs)
            setPluginReady(true)
            setLoading(false)
        })
    }, [show, cryptoService])

    useEffect(() => {
        if (!show) return
        loadHtml()
    }, [show])

    const loadHtml = async () => {
        setLoading(true)
        try {
            const response = await api('documents', 'get', {
                article: is_sign?.document_article,
            })
            const docTemplate = response.data?.[0]

            const html = await autocompleteDocument(
                docTemplate,
                visitId ?? 0,
                'visits',
                currentUser,
                {isPrinting: true, isClear: true},
                formData
            )
            setHtmlContent(html)
        } catch (e) {
            getErrorToast(intl.formatMessage({id: 'AUTH.SERVER.SERVER_ERROR'}))
        } finally {
            setLoading(false)
        }
    }

    const handleSign = async () => {
        setLoading(true)
        try {
            // Тут логика подписи
            setTimeout(() => {
                setLoading(false)
                onClose()
            }, 800)
        } catch (e) {
            setLoading(false)
        }
    }

    return (
        <Modal show={show} onHide={onClose} size='lg' centered backdrop='static' keyboard={false}>
            <Modal.Header closeButton>
                <Modal.Title>
                    {step === 1 ? 'Предпросмотр протокола' : 'Подписание документа'}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {isLoading ? (
                    <div className='d-flex justify-content-center py-5'>
                        <Spinner animation='border' />
                    </div>
                ) : (
                    <>
                        {step === 1 && (
                            <div
                                className='p-3 border rounded'
                                style={{maxHeight: '500px', overflow: 'auto'}}
                                dangerouslySetInnerHTML={{__html: htmlContent}}
                            />
                        )}

                        {step === 2 && (
                            <div>
                                <h5>Выберите сертификат</h5>

                                {certs.map((cert) => (
                                    <div
                                        key={cert.thumbprint}
                                        className={`cert-item ${
                                            selectedThumbprint === cert.thumbprint ? 'active' : ''
                                        }`}
                                        onClick={() => setSelectedThumbprint(cert.thumbprint)}
                                    >
                                        <b>{cert.subject}</b>
                                        <div>
                                            Действителен: {cert.validFrom} — {cert.validTo}
                                        </div>
                                        <div>Выдан: {cert.issuer}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                {step === 1 && (
                    <>
                        <ComponentButton
                            type='custom'
                            settings={{title: 'Отмена', background: 'secondary', icon: ''}}
                            customHandler={onClose}
                        />
                        <ComponentButton
                            type='custom'
                            settings={{title: 'Продолжить', background: 'dark', icon: ''}}
                            customHandler={() => setStep(2)}
                        />
                    </>
                )}

                {step === 2 && (
                    <>
                        <ComponentButton
                            type='custom'
                            settings={{title: 'Назад', background: 'secondary', icon: ''}}
                            customHandler={() => setStep(1)}
                        />
                        <ComponentButton
                            type='custom'
                            settings={{title: 'Подписать', background: 'dark', icon: ''}}
                            customHandler={handleSign}
                            loading={isLoading}
                        />
                    </>
                )}
            </Modal.Footer>
        </Modal>
    )
}

export default SignModalWizard
